"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useLanguage } from "../context/LanguageContext";

/* ==================================================================
   �� HERO BACKGROUND VIDEO
   ================================================================== */

const HERO_BG_VIDEO = "/hero.mp4";

/* Fallback video used only for the "Watch video" inline player */
 
/* ==================================================================
   TYPES
   ================================================================== */

interface ImageItem {
  id: number;
  url: string;
}

interface HeroData {
  title: string;
  subtitle: string;
  cta_primary: string;
  cta_secondary: string;
  images: ImageItem[];
  video_url?: string | null;
}

/* ==================================================================
   TRANSLATIONS
   ================================================================== */

const featureTranslations = {
  fr: {
    distributors_title: "Des distributeurs",
    distributors_sub: "proches de chez vous",
    question_title: "Une question ?",
    question_sub: "Contactez-nous",
    watch_video: "Voir la vidéo",
    close_video: "Fermer",
  },

  en: {
    distributors_title: "Distributors",
    distributors_sub: "near you",
    question_title: "A question?",
    question_sub: "Contact us",
    watch_video: "Watch video",
    close_video: "Close",
  },

  ar: {
    distributors_title: "موزعون",
    distributors_sub: "قريبون منك",
    question_title: "هل لديك سؤال؟",
    question_sub: "اتصل بنا",
    watch_video: "شاهد الفيديو",
    close_video: "إغلاق",
  },
};

/* ==================================================================
   ICONS
   ================================================================== */

function MapPinIcon() {
  return (
    <svg
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      strokeWidth={1.8}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 21s-7-5.686-7-11a7 7 0 1 1 14 0c0 5.314-7 11-7 11Z"
      />

      <circle
        cx="12"
        cy="10"
        r="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function QuestionIcon() {
  return (
    <svg
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      strokeWidth={1.8}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5Z"
      />

      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.5 9.5a2.5 2.5 0 0 1 4.7 1c0 1.5-2.2 2-2.2 3"
      />

      <circle
        cx="12"
        cy="17"
        r="0.6"
        fill="currentColor"
      />
    </svg>
  );
}

function PlayIcon({
  className = "h-5 w-5",
}: {
  className?: string;
}) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M8 5.14v13.72a1 1 0 0 0 1.54.84l10.4-6.86a1 1 0 0 0 0-1.68L9.54 4.3A1 1 0 0 0 8 5.14Z" />
    </svg>
  );
}

function CloseIcon({
  className = "h-4 w-4",
}: {
  className?: string;
}) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      strokeWidth={2.2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 6l12 12M6 18L18 6"
      />
    </svg>
  );
}

/* ==================================================================
   FEATURE PILL
   ================================================================== */

function FeaturePill({
  icon,
  textone,
  texttwo,
  href,
}: {
  icon: "map" | "question";
  textone: string;
  texttwo: string;
  href?: string;
}) {
  const inner = (
    <>
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white/10 text-white ring-1 ring-white/10 transition-all duration-300 group-hover:bg-white/20 group-hover:ring-white/20">
        {icon === "map" ? <MapPinIcon /> : <QuestionIcon />}
      </span>

      <span className="min-w-0 text-start">
        <span className="block text-[12px] font-semibold leading-tight text-white">
          {textone}
        </span>

        <span className="mt-0.5 block text-[10px] leading-tight text-white/55">
          {texttwo}
        </span>
      </span>
    </>
  );

  const base =
    "group flex items-center gap-2.5 rounded-2xl border border-white/10 bg-white/[0.04] px-2.5 py-2 backdrop-blur-md transition-all duration-300 hover:border-white/20 hover:bg-white/[0.09]";

  if (href) {
    return (
      <Link href={href} className={base}>
        {inner}
      </Link>
    );
  }

  return <div className={base}>{inner}</div>;
}

/* ==================================================================
   INLINE VIDEO PLAYER
   ================================================================== */

function InlineVideoPlayer({
  src,
  onClose,
  isRTL,
  closeLabel,
}: {
  src: string;
  onClose: () => void;
  isRTL: boolean;
  closeLabel: string;
}) {
  const isEmbed =
    /youtube|youtu\.be|vimeo|dailymotion/i.test(src);

  return (
    <div className="relative w-full max-w-md overflow-hidden rounded-2xl border border-white/15 bg-black/60 shadow-2xl shadow-black/50 backdrop-blur-md">
      {/* Close button */}

      <button
        type="button"
        onClick={onClose}
        aria-label={closeLabel}
        className="absolute end-2 top-2 z-20 grid h-8 w-8 place-items-center rounded-full border border-white/25 bg-black/60 text-white backdrop-blur-md transition-all duration-200 hover:scale-110 hover:bg-black/80"
      >
        <CloseIcon className="h-4 w-4" />
      </button>

      {/* Header */}

      <div className="flex items-center gap-2 px-3 pb-1.5 pt-2">
        <span className="h-px w-5 bg-white/50" />

        <span className="text-[9px] font-medium uppercase tracking-[0.25em] text-white/60">
          {isRTL ? "فيديو الشركة" : "Corporate Video"}
        </span>
      </div>

      {/* Video */}

      <div className="relative aspect-video w-full">
        {isEmbed ? (
          <iframe
            src={src}
            title="Corporate video"
            className="absolute inset-0 h-full w-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <video
            src={src}
            controls
            autoPlay
            playsInline
            className="absolute inset-0 h-full w-full object-contain"
          />
        )}
      </div>
    </div>
  );
}

/* ==================================================================
   HERO COMPONENT
   ================================================================== */

export default function Hero() {
  const { language } = useLanguage();

  const [data, setData] = useState<HeroData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showVideo, setShowVideo] = useState(false);

  const videoRef =
    useRef<HTMLVideoElement | null>(null);

  const isRTL = language === "ar";

  const t =
    featureTranslations[
      language as "fr" | "en" | "ar"
    ] ?? featureTranslations.en;

  /* ================================================================
     LOAD HERO DATA
     ================================================================ */

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);

      try {
        const res = await fetch(
          `/api/HomePage?locale=${language}`,
          {
            cache: "no-store",
          }
        );

        if (!res.ok) {
          throw new Error(
            `Hero API error: ${res.status}`
          );
        }

        const json = await res.json();

        if (cancelled) return;

        const item = json?.data?.[0];

        /* ------------------------------------------------------------
           DATA FOUND
           ------------------------------------------------------------ */

        if (item) {
          const images: ImageItem[] = (
            item.image ?? []
          ).map(
            (img: {
              id: number;
              url: string;
            }) => ({
              id: img.id,
              url: `${process.env.NEXT_PUBLIC_STRAPI_URL}${img.url}`,
            })
          );

          const rawVideo =
            item.video?.url ||
            item.video_url ||
            item.video?.[0]?.url ||
            null;

          const apiVideo = rawVideo
            ? rawVideo.startsWith("http")
              ? rawVideo
              : `${process.env.NEXT_PUBLIC_STRAPI_URL}${rawVideo}`
            : null;

          setData({
            title: item.title ?? "",
            subtitle: item.subtitle ?? "",
            cta_primary:
              item.cta_primary ?? "Explore",
            cta_secondary:
              item.cta_secondary ?? "About",
            images,
            video_url:
              apiVideo || HERO_BG_VIDEO,
          });
        }

        /* ------------------------------------------------------------
           NO DATA
           ------------------------------------------------------------ */

        else {
          setData({
            title: "Welcome",
            subtitle: "",
            cta_primary: "Explore",
            cta_secondary: "About",
            images: [],
            video_url: HERO_BG_VIDEO,
          });
        }
      } catch (err) {
        console.error(
          "Hero loading error:",
          err
        );

        if (!cancelled) {
          setData(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [language]);

  /* ================================================================
     FORCE BACKGROUND VIDEO PLAY
     ================================================================ */

  useEffect(() => {
    const video = videoRef.current;

    if (!video) return;

    video.muted = true;

    const playPromise = video.play();

    if (
      playPromise &&
      typeof playPromise.catch === "function"
    ) {
      playPromise.catch(() => {});
    }
  }, []);

  /* ================================================================
     LOADING SCREEN
     ================================================================ */

  if (loading) {
    return (
      <section className="relative flex h-screen min-h-[620px] w-full items-center justify-center bg-neutral-950">
        <div className="flex flex-col items-center gap-5">
          <span className="h-10 w-10 animate-spin rounded-full border-2 border-white/15 border-t-white" />

          <span className="text-[11px] font-medium uppercase tracking-[0.25em] text-white/50">
            Loading
          </span>
        </div>
      </section>
    );
  }

  /* ================================================================
     ERROR SCREEN
     ================================================================ */

  if (!data) {
    return (
      <section className="relative flex h-screen min-h-[620px] w-full items-center justify-center bg-neutral-950 text-white">
        <div className="text-center">
          <div className="mb-4 text-5xl opacity-60">
            ��
          </div>

          <p className="text-sm uppercase tracking-[0.25em] text-white/50">
            {language}
          </p>
        </div>
      </section>
    );
  }

  /* ================================================================
     MAIN HERO

     VIDEO:
     1920 × 1080
     16 : 9

     IMPORTANT:
     aspect-[16/9] + object-contain
     = full video visible
     = no logo cropping
     ================================================================ */

  return (
    // <section className="relative w-full aspect-[16/9] overflow-hidden bg-neutral-950">
<section
  className="relative w-full aspect-[1920/844] overflow-hidden bg-neutral-950"
> 
      {/* ============================================================
          BACKGROUND VIDEO
          ============================================================ */}

      <div className="absolute inset-0 flex items-center justify-center bg-neutral-950">
        <video
          ref={videoRef}
          src={HERO_BG_VIDEO}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="h-full w-full object-contain"
          style={{
            filter:
              "brightness(1.02) contrast(1.05)",
          }}
        />
      </div>

      {/* ============================================================
          MAIN GRADIENT
          ============================================================ */}

      <div
        className={`absolute inset-0 ${
          isRTL
            ? "bg-gradient-to-l"
            : "bg-gradient-to-r"
        } from-black/65 via-black/20 to-transparent`}
      />

      {/* ============================================================
          BOTTOM GRADIENT
          ============================================================ */}

      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/55 to-transparent" />

      {/* ============================================================
          CONTENT
          ============================================================ */}

      <div
        dir={isRTL ? "rtl" : "ltr"}
        className="relative z-10 flex h-full w-full items-center"
      >
        <div className="mx-auto w-full max-w-7xl px-5 sm:px-8 lg:px-12">

          <div
            className={`grid w-full grid-cols-1 items-center gap-8 lg:grid-cols-12 ${
              isRTL
                ? "text-right"
                : "text-left"
            }`}
          >

            {/* ======================================================
                LEFT CONTENT
                ======================================================  

            <div className="lg:col-span-7 xl:col-span-6">

              <div className="relative rounded-3xl border border-white/15 bg-transparent p-6 backdrop-blur-[2px] sm:p-8 lg:p-10">

              

                <h1
                  className="text-4xl font-semibold leading-[1.05] tracking-tight text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.6)] sm:text-5xl lg:text-6xl xl:text-7xl"
                  style={{
                    textWrap: "balance",
                  }}
                >
                  {data.title}
                </h1>

               

                <p className="mt-5 max-w-xl text-base leading-relaxed text-white/90 drop-shadow-[0_1px_8px_rgba(0,0,0,0.6)] sm:text-lg">
                  {data.subtitle}
                </p>

                 ==================================================
                    CTA BUTTONS
                    ==================================================  

                <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">

 
                  <a
                    href="#specialties"
                    className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-white px-6 py-3 text-sm font-semibold text-neutral-900 shadow-lg shadow-black/40 transition-all duration-300 hover:bg-white/90 hover:shadow-xl active:scale-[0.98]"
                  >
                    <span>
                      {data.cta_primary}
                    </span>

                    <svg
                      className={`h-4 w-4 transition-transform duration-300 ${
                        isRTL
                          ? "rotate-180 group-hover:-translate-x-1"
                          : "group-hover:translate-x-1"
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 7l5 5m0 0l-5 5m5-5H6"
                      />
                    </svg>
                  </a>

 
                  <Link
                    href="/about"
                    className="group inline-flex items-center justify-center gap-2.5 rounded-full border border-white/60 bg-transparent px-6 py-3 text-sm font-semibold text-white backdrop-blur-sm transition-all duration-300 hover:border-white/90 hover:bg-white/10 active:scale-[0.98]"
                  >
                    <span>
                      {data.cta_secondary}
                    </span>

                    <svg
                      className={`h-4 w-4 transition-transform duration-300 ${
                        isRTL
                          ? "rotate-180 group-hover:-translate-x-1"
                          : "group-hover:translate-x-1"
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </Link>
 
                </div>
                 



















                

                <div className="mt-6 grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:hidden">

                  <FeaturePill
                    icon="map"
                    textone={
                      t.distributors_title
                    }
                    texttwo={
                      t.distributors_sub
                    }
                    href="/locate-us"
                  />

                  <FeaturePill
                    icon="question"
                    textone={
                      t.question_title
                    }
                    texttwo={
                      t.question_sub
                    }
                    href="/contact"
                  />

                </div>
              </div>
            </div>*/}

            {/* ======================================================
                RIGHT — OPTIONAL VIDEO
                ====================================================== */}

            <div className="lg:col-span-5 xl:col-span-6">

              {showVideo && (
                <div
                  className={`flex justify-center ${
                    isRTL
                      ? "lg:justify-start"
                      : "lg:justify-end"
                  }`}
                >
                  <InlineVideoPlayer
                    src={
                      data.video_url ||
                      HERO_BG_VIDEO
                    }
                    onClose={() =>
                      setShowVideo(false)
                    }
                    isRTL={isRTL}
                    closeLabel={
                      t.close_video
                    }
                  />
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

      {/* ============================================================
          DESKTOP BOTTOM FEATURE PILLS
          ============================================================ */}

      <div className="pointer-events-none absolute inset-x-0 bottom-6 z-20 hidden lg:block">

        <div
          dir={isRTL ? "rtl" : "ltr"}
          className="pointer-events-auto mx-auto w-full max-w-7xl px-12"
        >

          <div className="flex items-center justify-end gap-3">

            <FeaturePill
              icon="map"
              textone={
                t.distributors_title
              }
              texttwo={
                t.distributors_sub
              }
              href="/locate-us"
            />

            <FeaturePill
              icon="question"
              textone={
                t.question_title
              }
              texttwo={
                t.question_sub
              }
              href="/contact"
            />

          </div>
        </div>
      </div>

    </section>
  );
}